import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import config from "../../../config";
import { ConversationLoader } from "../../components/OlliLogo";
import { PrimaryButton } from "../../components/ui";
import { UndoToast } from "../../components/crm/ui";
import { TextInput } from "../../components/crm/leadsKit";
import { ConnectFrame, ConnectGate, ErrorNote, NO_URL, googleWebhookUrl, useIntegration } from "../../components/settings/integrationsKit";

/**
 * Settings → Integrations → :id → Connect Google Ads (design: IntegrationConnect.tsx, "google-ads").
 *
 * The flow is the old ConnectGoogle.js, unchanged:
 *   "Sign in with Google" → GET /api/integrations/google/auth-url?integration_id=:id,
 *     then window.location.href = auth_url (full-page redirect to Google);
 *   back on this route with ?code=… → POST /api/integrations/google/callback { code, integration_id };
 *     Google comes back here directly, through the backend's GET /api/integrations/google/oauth-return,
 *     or through the panel's /settings/integrations/google-callback (GoogleCallback.js); the last two
 *     bounce here with ?code= or ?error=. The code is dropped from the address once read, so a reload
 *     doesn't send a used code again. A failed exchange or a cancelled sign-in is shown on the card.
 *   manual setup → POST /api/integrations/:id/connect-google with the old payload.
 * The webhook URL is built as before: /api/webhooks/google/{org}/{id}.
 * The design's "Choose an ad account" / "Choose lead form assets" steps have no
 * API behind them (Google's callback connects the account directly), so they are not shown.
 */

export default function ConnectGoogle() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { row: integration, loading, reload } = useIntegration(id);
  const [connecting, setConnecting] = useState(false);
  const [waiting, setWaiting] = useState(false);
  const [manual, setManual] = useState(false);
  const [manualAccountId, setManualAccountId] = useState("");
  const [manualAccountName, setManualAccountName] = useState("");
  const [toast, setToast] = useState(null);
  const [oauthError, setOauthError] = useState("");
  const usedCode = useRef(null); // a code is exchanged once, even when the effect runs twice
  const showToast = (message) => setToast({ message });

  // ---- Copied from the old ConnectGoogle.js ----

  useEffect(() => {
    const code = searchParams.get("code");
    const denied = searchParams.get("error");
    if (!code && !denied) return;
    setSearchParams({}, { replace: true });
    if (code && usedCode.current !== code) { usedCode.current = code; handleOAuthCallback(code); }
    else setOauthError(denied === "access_denied" ? "Google sign-in was cancelled. Sign in again to connect, or use manual setup." : `Google didn't connect (${denied}). Try again, or use manual setup.`);
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleGoogleLogin = async () => {
    setConnecting(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(
        `${config.API_BASE_URL}/api/integrations/google/auth-url?integration_id=${id}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data.success) {
        window.location.href = response.data.data.auth_url;
      } else if (response.data.manual_only) {
        setManual(true);
        showToast("Google OAuth not configured. Use manual setup.");
      }
    } catch (err) {
      showToast("Google OAuth not available. Use manual setup.");
      setManual(true);
    } finally {
      setConnecting(false);
    }
  };

  const handleOAuthCallback = async (code) => {
    setConnecting(true);
    setWaiting(true);
    setOauthError("");
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${config.API_BASE_URL}/api/integrations/google/callback`,
        { code, integration_id: id },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data.success) {
        showToast("Google Ads connected successfully!");
        reload();
      }
    } catch (err) {
      const d = err.response?.data;
      setOauthError(`${d?.message || "Failed to connect"}${d?.error ? `: ${d.error}` : ""}. Sign in again, or use manual setup.`);
    } finally {
      setConnecting(false);
      setWaiting(false);
    }
  };

  const handleManualConnect = async () => {
    if (!manualAccountId) {
      showToast("Please enter your Google Ads Customer ID");
      return;
    }
    setConnecting(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${config.API_BASE_URL}/api/integrations/${id}/connect-google`,
        {
          access_token: "manual_setup",
          refresh_token: null,
          google_account_id: manualAccountId,
          google_account_name: manualAccountName || `Google Ads (${manualAccountId})`,
          customer_id: manualAccountId,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (response.data.success) {
        showToast("Google Ads connected successfully!");
        reload();
      }
    } catch (err) {
      showToast(err.response?.data?.message || "Failed to connect");
    } finally {
      setConnecting(false);
    }
  };

  // ---- Design markup ----

  const endpoint = googleWebhookUrl(id);
  const secret = integration?.webhook_secret || "";
  const copy = (value) => { if (!value) return; void navigator.clipboard?.writeText(value); showToast("Copied to clipboard"); };
  const connected = !!integration?.is_connected;
  const webhookPanel = <><p className="text-small font-semibold">Webhook setup</p><p className="mt-1 text-caption text-muted">Paste these details in your Google Ads lead form asset under Webhook integration.</p><div className="mt-3 flex items-center gap-2"><code className="min-w-0 flex-1 truncate font-mono text-caption">{endpoint || NO_URL}</code><button type="button" onClick={() => copy(endpoint)} className="text-caption font-semibold">Copy</button></div>{secret && <div className="mt-2 flex items-center gap-2"><code className="min-w-0 flex-1 truncate font-mono text-caption">{secret}</code><button type="button" onClick={() => copy(secret)} className="text-caption font-semibold">Copy</button></div>}</>;

  return <ConnectGate loading={loading} row={integration}>
    <ConnectFrame monogram="GA" title="Connect Google Ads" description="Capture leads from Google Ads lead form assets.">
      <div className="mt-5 rounded-md bg-mist p-4"><h2 className="text-small font-semibold">How it works</h2><ol className="mt-2 list-inside list-decimal space-y-1.5 text-small text-muted"><li>Sign in with Google</li><li>Choose your ad account</li><li>Select the lead form assets to sync</li><li>Leads arrive in Olli automatically</li></ol></div>
      {waiting && <div className="flex flex-col items-center py-12"><ConversationLoader size={42} tone="black" /><p className="mt-4 text-small font-semibold">Waiting for Google</p></div>}
      {!waiting && <ErrorNote>{oauthError}</ErrorNote>}{!waiting && connected && <><div className="mt-5 rounded-md bg-mist p-4">{webhookPanel}</div><PrimaryButton className="mt-5 w-full" onClick={() => navigate(`/settings/integrations/${id}`, { state: { connected: "Google Ads" } })}>Continue</PrimaryButton></>}
      {!waiting && !connected && <><PrimaryButton className="mt-5 w-full" loading={connecting} onClick={handleGoogleLogin}>Sign in with Google</PrimaryButton><button type="button" onClick={() => setManual(!manual)} className="mt-3 w-full py-2 text-caption font-medium text-muted">{manual ? "Hide manual setup" : "Manual setup (advanced)"}</button>{manual && <div className="mt-2 rounded-md bg-mist p-4">{webhookPanel}<div className="mt-4 grid gap-3"><label className="text-caption font-semibold">Google Ads Customer ID *<TextInput value={manualAccountId} onChange={(e) => setManualAccountId(e.target.value)} placeholder="Google Ads, Settings, Account settings" className="mt-1" /></label><label className="text-caption font-semibold">Account name (optional)<TextInput value={manualAccountName} onChange={(e) => setManualAccountName(e.target.value)} className="mt-1" /></label></div><button type="button" disabled={connecting} onClick={handleManualConnect} className="mt-4 rounded-full px-4 py-2 text-small font-semibold ring-1 ring-line">I've added it</button></div>}</>}
    </ConnectFrame>
    {toast && <UndoToast message={toast.message} onDone={() => setToast(null)} />}
  </ConnectGate>;
}
